package dev.lingvo.site.pages.app.projects.slug.screenshots

import dev.lingvo.shared.screenshots.doc.LayerPatch
import dev.lingvo.shared.screenshots.doc.LocaleOverride
import dev.lingvo.shared.screenshots.doc.ScreenshotDesign
import dev.lingvo.site.components.studio.editor.pathTo
import dev.lingvo.shared.screenshots.render.upgradeLocaleOverrides
import dev.lingvo.site.i18n.tr
import dev.lingvo.site.i18n.t

/** A locale edit adopts expanded locale overrides deliberately and validates the same bytes as autosave. */
internal object DesignLocaleOverrides {
    fun patch(
        design: ScreenshotDesign, boardId: String, pageId: String, locale: String, layerId: String,
        change: (LayerPatch) -> LayerPatch,
    ): ScreenshotDesign {
        val page = requireNotNull(design.boards.firstOrNull { it.id == boardId }?.pages?.firstOrNull { it.id == pageId }) {
            tr("app.projects.slug.screenshots.design_locale_overrides.choose_an_unlocked_layer", "Choose an unlocked layer.")
        }
        val path = pathTo(page.layers, layerId)
        require(path.isNotEmpty() && path.none { it.base.locked }) { tr("app.projects.slug.screenshots.design_locale_overrides.choose_an_unlocked_layer", "Choose an unlocked layer.") }
        return edit(design, boardId, pageId, locale) { value ->
            value.copy(patches = value.patches + (layerId to change(value.patches[layerId] ?: LayerPatch())))
        }
    }

    fun reset(design: ScreenshotDesign, boardId: String, pageId: String, locale: String, layerId: String): ScreenshotDesign =
        edit(design, boardId, pageId, locale) { it.copy(patches = it.patches - layerId) }

    fun mirror(design: ScreenshotDesign, boardId: String, pageId: String, locale: String, mirror: Boolean?): ScreenshotDesign =
        edit(design, boardId, pageId, locale) { it.copy(mirror = mirror) }

    fun inheritance(design: ScreenshotDesign, boardId: String, pageId: String, locale: String, enabled: Boolean): ScreenshotDesign =
        edit(design, boardId, pageId, locale) { it.copy(inheritLanguage = enabled) }

    private fun edit(
        design: ScreenshotDesign, boardId: String, pageId: String, locale: String,
        change: (LocaleOverride) -> LocaleOverride,
    ): ScreenshotDesign {
        val adopted = upgradeLocaleOverrides(design)
        require(adopted.boards.any { it.id == boardId && it.pages.any { page -> page.id == pageId } }) {
            "Choose a page in this design." // i18n-ignore: internal stale-target validation
        }
        return checked(adopted.copy(boards = adopted.boards.map { board ->
            if (board.id != boardId) board else board.copy(pages = board.pages.map { page ->
                if (page.id != pageId) page else page.copy(overrides = page.overrides +
                    (locale to change(page.overrides[locale] ?: LocaleOverride())))
            })
        }))
    }
}
